import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  type SelectLocationProperties,
  type SelectLocationSources,
} from "@/analytics/generic-events-properties";
import { IElementImg } from "@/hooks/use-placeholders";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  IElementOrthomosaic,
  IElementPhotogrammetryScan,
  isIElementOrthomosaic,
} from "@/types/ielement-extra-types";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  IElement,
  IElementGaussianSplats,
  IElementGenericStream,
  isIElementGaussianSplats,
  isIElementGenericStream,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { useCallback } from "react";

/** An element that can be rendered as the active element of a flyWalk scene */
export type FlyWalkSceneActiveElement =
  | IElementImg
  | IElementGenericStream
  | IElementOrthomosaic
  | IElementGaussianSplats;

/** A waypoint that can be clicked on the minimap or in the 3D scene */
export type FlyWalkWaypoint = IElementImg | IElementPhotogrammetryScan;

/**
 * @param element to check
 * @returns true if it's a valid FlyWalkSceneActiveElement
 */
export function isFlyWalkSceneActiveElement(
  element: IElement,
): element is FlyWalkSceneActiveElement {
  return (
    isFlyWalkImageElement(element) ||
    isIElementGenericStream(element) ||
    isIElementGaussianSplats(element)
  );
}

/**
 *
 * @param element Element to check
 * @returns Whether the element is an image type (pano, photo, orthomosaic)
 */
export function isFlyWalkImageElement(
  element: IElement,
): element is IElementImg | IElementOrthomosaic {
  return (
    isIElementImg360(element) ||
    isIElementImg2d(element) ||
    isIElementOrthomosaic(element)
  );
}

/** Default duration of animations in flywalk mode, in seconds */
export const ANIMATION_DURATION = 1;

type TrackerFnc = (via: SelectLocationSources) => void;

/**  @returns a callback useful to track the SelectLocation amplitude event. */
export function useTrackSelectLocation(): TrackerFnc {
  const activeTool = useAppSelector(selectActiveTool);

  return useCallback(
    (via: SelectLocationSources) => {
      Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
        via,
        tool: computeToolPropertyForSelectLocationEvent(activeTool),
      });
    },
    [activeTool],
  );
}
